/**
 * Applicant list / main dashboard
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Alert, Badge, Button, Card, EmptyState } from '../components/ui.jsx';
import {
  STAGE_LABELS, SOURCE_LABELS,
} from '../utils/format';
import {
  getApplicantProductivityFlags,
  getBulkSelectionSummary,
  isApplicantSelectableForBulkMessage,
} from '../utils/applicantProductivity';
import {
  APPLICANT_PAGE_SIZE,
  getApplicantQueryKey,
  getNextApplicantPage,
  mergeApplicantPages,
  shouldLoadMoreApplicants,
} from '../utils/applicantListPaging';
import {
  buildApplicantQueueCards,
  getApplicantDisplayName,
  getApplicantInitials,
  getApplicantStageBadgeVariant,
  getApplicantWarningLabel,
} from '../utils/applicantDashboardUi';
import {
  ATTENTION_FILTER_OPTIONS,
  CAMPAIGN_FILTER_OPTIONS,
  NEXT_ACTION_FILTER_OPTIONS,
  getAttentionFilterParams,
  getAttentionFilterValue,
  getCampaignBadge,
  getWorkflowEmptyState,
  getWorkflowInterviewSummary,
  getWorkflowLastTouch,
  getWorkflowNextAction,
  getWorkflowStageLabel,
} from '../utils/applicantWorkflowFilters';

const STAGES = ['ALL', 'NEW', 'CONTACTED', 'SCREENING', 'INTERVIEW_SCHEDULED',
  'INTERVIEWED', 'HIRED', 'ONBOARDING', 'REJECTED', 'NO_SHOW', 'UNRESPONSIVE'];

const QUEUE_TONE_DOTS = {
  info: 'bg-brand-blue',
  warning: 'bg-warning-text',
  purple: 'bg-purple-600',
  danger: 'bg-danger-text',
};

function cx(...classes) {
  return classes.flat().filter(Boolean).join(' ');
}

function formatDateInputInEastern(date) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export default function ApplicantList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [applicants, setApplicants] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkBody, setBulkBody] = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [toast, setToast] = useState('');
  const searchInputRef = useRef(null);
  const loadMoreRef = useRef(null);

  const stage = searchParams.get('stage') || 'ALL';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';
  const dateFrom = searchParams.get('date_from') || '';
  const dateTo = searchParams.get('date_to') || '';
  const source = searchParams.get('source') || '';
  const attention = searchParams.get('attention') || '';
  const attentionReason = searchParams.get('attention_reason') || '';
  const campaignStatus = searchParams.get('campaign_status') || '';
  const nextAction = searchParams.get('next_action') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const activeQueryKey = getApplicantQueryKey({ stage, search, sort, dateFrom, dateTo, source, attention, attentionReason, campaignStatus, nextAction });
  const latestQueryKeyRef = useRef(activeQueryKey);
  latestQueryKeyRef.current = activeQueryKey;
  const hasMoreApplicants = shouldLoadMoreApplicants({
    loadedCount: applicants.length,
    total,
    loading: loading || loadingMore,
  });

  const fetchApplicants = useCallback(async () => {
    const isFirstPage = page === 1;
    setLoading(isFirstPage);
    setLoadingMore(!isFirstPage);
    setError('');
    try {
      const requestQueryKey = activeQueryKey;
      const params = new URLSearchParams();
      if (stage !== 'ALL') params.set('stage', stage);
      if (search) params.set('search', search);
      if (sort) params.set('sort', sort);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (source) params.set('source', source);
      if (attention) params.set('attention', attention);
      if (attentionReason) params.set('attention_reason', attentionReason);
      if (campaignStatus) params.set('campaign_status', campaignStatus);
      if (nextAction) params.set('next_action', nextAction);
      params.set('page', String(page));
      params.set('limit', String(APPLICANT_PAGE_SIZE));

      const data = await api.get(`/applicants?${params}`);
      if (latestQueryKeyRef.current !== requestQueryKey) return;
      setApplicants(current => isFirstPage
        ? (data.applicants || [])
        : mergeApplicantPages(current, data.applicants || [])
      );
      setStageCounts(data.stageCounts || {});
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [stage, search, sort, dateFrom, dateTo, source, attention, attentionReason, campaignStatus, nextAction, page, activeQueryKey]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  useEffect(() => {
    const visibleIds = new Set(applicants.map(applicant => applicant.id));
    setSelectedIds(ids => ids.filter(id => visibleIds.has(id)));
  }, [applicants]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowBulkModal(false);
        return;
      }

      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const isTyping = ['input', 'textarea', 'select'].includes(activeTag);
      if (!isTyping && event.key === '/') {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const showToast = (message) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  };

  const updateParam = useCallback((key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const updateParams = useCallback((values) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(values)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    next.delete('page');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  const loadNextApplicantPage = useCallback(() => {
    const nextPage = getNextApplicantPage({
      page,
      loadedCount: applicants.length,
      total,
      loading: loading || loadingMore,
    });
    if (nextPage !== page) updateParam('page', String(nextPage));
  }, [applicants.length, loading, loadingMore, page, total, updateParam]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMoreApplicants) return undefined;

    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) loadNextApplicantPage();
    }, { rootMargin: '320px 0px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMoreApplicants, loadNextApplicantPage]);

  const updateDatePreset = (from) => {
    const next = new URLSearchParams(searchParams);
    if (from) next.set('date_from', from);
    else next.delete('date_from');
    next.delete('date_to');
    next.delete('page');
    setSearchParams(next);
  };

  const totalCount = Object.values(stageCounts).reduce((a, b) => a + b, 0);
  const selectedApplicants = applicants.filter(applicant => selectedIds.includes(applicant.id));
  const bulkSummary = getBulkSelectionSummary(selectedApplicants);
  const contactableApplicants = applicants.filter(isApplicantSelectableForBulkMessage);
  const allContactableVisibleSelected = contactableApplicants.length > 0 && contactableApplicants.every(applicant => selectedIds.includes(applicant.id));
  const activeFilters = [search, stage !== 'ALL' ? stage : '', dateFrom, dateTo, source, attention, attentionReason, campaignStatus, nextAction].filter(Boolean).length;
  const activeAttentionFilter = getAttentionFilterValue({ attention, attentionReason });
  const emptyState = getWorkflowEmptyState({ attention, attentionReason, campaignStatus });

  const toggleSelected = (applicantId) => {
    setSelectedIds(ids => ids.includes(applicantId)
      ? ids.filter(id => id !== applicantId)
      : [...ids, applicantId]
    );
  };

  const toggleAllVisible = () => {
    if (allContactableVisibleSelected) {
      const visibleIds = new Set(contactableApplicants.map(applicant => applicant.id));
      setSelectedIds(ids => ids.filter(id => !visibleIds.has(id)));
      return;
    }
    setSelectedIds(ids => Array.from(new Set([...ids, ...contactableApplicants.map(applicant => applicant.id)])));
  };

  const clearFilters = () => {
    setSearchParams(new URLSearchParams(sort ? [['sort', sort]] : []));
  };

  const handleBulkSend = async () => {
    if (!bulkBody.trim() || bulkSummary.contactableCount === 0 || bulkSending) return;
    setBulkSending(true);
    try {
      const result = await api.post('/messages/bulk', {
        applicant_ids: bulkSummary.contactableIds,
        body: bulkBody,
      });
      setBulkBody('');
      setShowBulkModal(false);
      setSelectedIds([]);
      await fetchApplicants();
      const sent = result?.results?.sent ?? bulkSummary.contactableCount;
      const skipped = result?.results?.skipped ?? bulkSummary.skippedCount;
      showToast(`Bulk text sent to ${sent}${skipped ? `; ${skipped} skipped` : ''}`);
    } catch (err) {
      alert(err.message);
    } finally {
      setBulkSending(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-5 lg:px-6">
        <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {buildApplicantQueueCards(applicants).map(card => (
            <button
              type="button"
              key={card.label}
              onClick={() => updateParam('stage', card.stageFilter)}
              className="rounded-card border border-border-default bg-white p-4 text-left shadow-card transition-colors hover:bg-surface-hover focus-ring"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">{card.label}</span>
                <span className={cx('h-2.5 w-2.5 rounded-full', QUEUE_TONE_DOTS[card.tone])} aria-hidden="true" />
              </div>
              <p className="mt-2 text-2xl font-bold tracking-[-0.02em] text-text-primary">{card.count}</p>
              <p className="mt-1 text-xs text-text-muted">From {applicants.length} loaded rows</p>
            </button>
          ))}
        </div>

        <Card className="mb-5" padding="compact">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[240px] flex-1">
                <label htmlFor="applicant-search" className="sr-only">Search applicants</label>
                <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-text-muted">⌕</span>
                <input
                  id="applicant-search"
                  ref={searchInputRef}
                  type="search"
                  placeholder="Search applicants — press / to focus"
                  value={search}
                  onChange={e => updateParam('search', e.target.value)}
                  className="input pl-8"
                />
              </div>
              <select
                value={sort}
                onChange={e => updateParam('sort', e.target.value)}
                aria-label="Sort applicants"
                className="input bg-white sm:w-48"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="days_in_stage">Days in stage</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                { label: 'All time', days: null },
                { label: '7 days', days: 7 },
                { label: '30 days', days: 30 },
                { label: '90 days', days: 90 },
              ].map(({ label, days }) => {
                const from = days ? formatDateInputInEastern(new Date(Date.now() - days * 86400000)) : '';
                const isActive = days
                  ? dateFrom === from && dateTo === ''
                  : dateFrom === '' && dateTo === '';
                return (
                  <button
                    key={label}
                    type="button"
                    onClick={() => updateDatePreset(from)}
                    className={cx(
                      'min-h-9 rounded-app border px-3 text-sm font-semibold transition-colors focus-ring',
                      isActive
                        ? 'border-info-border bg-brand-blueSoft text-brand-blue'
                        : 'border-border-default bg-white text-text-secondary hover:bg-surface-hover',
                    )}
                  >
                    {label}
                  </button>
                );
              })}
              {activeFilters > 0 && <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>}
            </div>
          </div>

          <div className="mt-4 flex gap-1 overflow-x-auto rounded-app border border-border-default bg-surface-subtle p-1" role="tablist" aria-label="Applicant stage filters">
            {STAGES.map(s => {
              const count = s === 'ALL' ? totalCount : (stageCounts[s] || 0);
              const isActive = stage === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => updateParam('stage', s === 'ALL' ? '' : s)}
                  role="tab"
                  aria-selected={isActive}
                  className={cx(
                    'min-h-8 flex-shrink-0 rounded-md border px-3 text-[13px] font-semibold transition-colors focus-ring',
                    isActive
                      ? 'border-border-default bg-white text-brand-blue shadow-sm'
                      : 'border-transparent text-text-muted hover:text-text-primary',
                  )}
                >
                  {s === 'ALL' ? 'All' : STAGE_LABELS[s]}
                  {count > 0 && <span className="ml-1.5 text-xs text-text-muted">{count}</span>}
                </button>
              );
            })}
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
              Attention
              <select
                value={activeAttentionFilter}
                onChange={event => updateParams(getAttentionFilterParams(event.target.value))}
                className="input mt-1 bg-white normal-case tracking-normal"
                aria-label="Filter by needs-attention status"
              >
                {ATTENTION_FILTER_OPTIONS.map(option => (
                  <option key={option.label} value={option.params.attention_reason || (option.params.attention ? 'attention' : '')}>{option.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
              Campaign
              <select
                value={campaignStatus}
                onChange={event => updateParam('campaign_status', event.target.value)}
                className="input mt-1 bg-white normal-case tracking-normal"
                aria-label="Filter by campaign status"
              >
                {CAMPAIGN_FILTER_OPTIONS.map(option => <option key={option.label} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label className="block text-xs font-semibold uppercase tracking-[0.08em] text-text-muted">
              Next action
              <select
                value={nextAction}
                onChange={event => updateParam('next_action', event.target.value)}
                className="input mt-1 bg-white normal-case tracking-normal"
                aria-label="Filter by next action"
              >
                {NEXT_ACTION_FILTER_OPTIONS.map(option => <option key={option.label} value={option.value}>{option.label}</option>)}
              </select>
            </label>
          </div>
        </Card>

        {error && <Alert variant="danger" className="mb-4" role="alert">{error}</Alert>}

        {selectedIds.length > 0 && (
          <Alert variant="info" className="sticky top-28 z-20 mb-4 shadow-card">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold">{bulkSummary.selectedCount} selected · {bulkSummary.contactableCount} textable</p>
                {bulkSummary.skippedCount > 0 && (
                  <p className="mt-0.5">{bulkSummary.skippedCount} selected applicant(s) are opted out or do-not-contact and will be skipped.</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => setShowBulkModal(true)} disabled={bulkSummary.contactableCount === 0} size="sm">
                  Send bulk text
                </Button>
                <Button type="button" variant="secondary" onClick={() => setSelectedIds([])} size="sm">
                  Clear selection
                </Button>
              </div>
            </div>
          </Alert>
        )}

        <Card padding="compact" className="overflow-hidden p-0">
          {loading ? (
            <EmptyState title="Loading applicants" description="Fetching the latest candidate queue." icon="↻" />
          ) : applicants.length === 0 ? (
            <EmptyState
              title={emptyState.title}
              description={search ? 'No loaded applicants match the current filters.' : emptyState.description}
              icon="👥"
              action={activeFilters > 0 ? <Button type="button" variant="secondary" onClick={clearFilters}>Clear filters</Button> : null}
            />
          ) : (
            <>
              <div className="divide-y divide-border-default md:hidden">
                {applicants.map(a => (
                  <ApplicantCard
                    key={a.id}
                    applicant={a}
                    selected={selectedIds.includes(a.id)}
                    selectable={isApplicantSelectableForBulkMessage(a)}
                    onToggle={() => toggleSelected(a.id)}
                    onOpen={() => navigate(`/applicants/${a.id}`)}
                  />
                ))}
              </div>

              <div className="hidden overflow-x-auto table-scrollbar md:block">
                <table className="w-full min-w-[980px] text-left">
                  <thead className="sticky top-0 bg-surface-subtle text-xs font-bold uppercase tracking-[0.04em] text-text-muted">
                    <tr className="border-b border-border-default">
                      <th className="w-10 px-3 py-3">
                        <input
                          type="checkbox"
                          checked={allContactableVisibleSelected}
                          disabled={contactableApplicants.length === 0}
                          onChange={toggleAllVisible}
                          aria-label="Select all textable visible applicants"
                          className="h-4 w-4 rounded border-border-strong text-brand-blue focus:ring-brand-blue disabled:opacity-40"
                        />
                      </th>
                      <th className="px-3 py-3">Applicant</th>
                      <th className="px-3 py-3">Workflow</th>
                      <th className="px-3 py-3">Next action</th>
                      <th className="px-3 py-3">Campaign</th>
                      <th className="px-3 py-3">Last touch</th>
                      <th className="px-3 py-3">Interview</th>
                      <th className="px-3 py-3">Flags</th>
                      <th className="px-3 py-3">Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-default text-[13px] text-text-secondary">
                    {applicants.map(a => (
                      <ApplicantRow
                        key={a.id}
                        applicant={a}
                        selected={selectedIds.includes(a.id)}
                        selectable={isApplicantSelectableForBulkMessage(a)}
                        onToggle={() => toggleSelected(a.id)}
                        onOpen={() => navigate(`/applicants/${a.id}`)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        {!loading && applicants.length > 0 && (
          <div ref={loadMoreRef} className="py-5 text-center text-sm text-text-muted" aria-live="polite">
            {loadingMore
              ? 'Loading more applicants...'
              : hasMoreApplicants
                ? 'Scroll for more applicants'
                : `All ${total} applicants loaded`}
          </div>
        )}
      </div>

      {toast && <Toast message={toast} />}

      {showBulkModal && (
        <Modal title="Send bulk text" onClose={() => setShowBulkModal(false)}>
          <div className="space-y-3">
            <Alert variant="info">
              Sending to {bulkSummary.contactableCount} applicant(s).
              {bulkSummary.skippedCount > 0 && ` ${bulkSummary.skippedCount} selected applicant(s) will be skipped.`}
            </Alert>
            <textarea
              value={bulkBody}
              onChange={event => setBulkBody(event.target.value)}
              rows={4}
              placeholder="Write the bulk text message..."
              className="input min-h-28 resize-none"
              autoFocus
            />
            <div className="flex gap-2">
              <Button type="button" onClick={handleBulkSend} disabled={!bulkBody.trim() || bulkSummary.contactableCount === 0 || bulkSending} loading={bulkSending} className="flex-1">
                Send bulk text
              </Button>
              <Button type="button" variant="secondary" onClick={() => setShowBulkModal(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

function ApplicantRow({ applicant, selected, selectable, onToggle, onOpen }) {
  const displayName = getApplicantDisplayName(applicant);
  const warning = getApplicantWarningLabel(applicant);
  const workflowLabel = getWorkflowStageLabel(applicant) || STAGE_LABELS[applicant.stage] || applicant.stage;
  const campaignBadge = getCampaignBadge(applicant.workflow);
  const next = getWorkflowNextAction(applicant);
  const lastTouch = getWorkflowLastTouch(applicant.workflow, applicant.last_contact);
  const interview = getWorkflowInterviewSummary(applicant.workflow);
  return (
    <tr
      onClick={onOpen}
      className="cursor-pointer transition-colors hover:bg-surface-hover"
    >
      <td className="px-3 py-2.5" onClick={event => event.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          disabled={!selectable}
          onChange={onToggle}
          aria-label={`Select ${displayName}`}
          className="h-4 w-4 rounded border-border-strong text-brand-blue focus:ring-brand-blue disabled:opacity-40"
        />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-blueSoft text-xs font-bold text-brand-blue ring-1 ring-info-border">
            {getApplicantInitials(applicant)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-semibold text-text-primary">{displayName}</p>
            <p className="truncate text-xs text-text-muted">{applicant.position || applicant.role || 'Driver / Operator'} · {SOURCE_LABELS[applicant.source] || applicant.source || 'Manual'}</p>
            <ApplicantProductivityBadges applicant={applicant} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5"><Badge variant={getApplicantStageBadgeVariant(applicant.workflow?.stageKey || applicant.stage)}>{workflowLabel}</Badge></td>
      <td className="px-3 py-2.5"><span className={cx('font-semibold', next.className)}>{next.label}</span></td>
      <td className="px-3 py-2.5">{campaignBadge ? <Badge variant={campaignBadge.variant}>{campaignBadge.label}</Badge> : <span className="text-text-muted">—</span>}</td>
      <td className="whitespace-nowrap px-3 py-2.5">{lastTouch}</td>
      <td className="whitespace-nowrap px-3 py-2.5">{interview}</td>
      <td className="px-3 py-2.5">{warning ? <Badge variant="danger">{warning}</Badge> : applicant.workflow?.needsAttention ? <Badge variant="warning">Needs attention</Badge> : <span className="text-text-muted">—</span>}</td>
      <td className="px-3 py-2.5" onClick={event => event.stopPropagation()}>
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open ${displayName} applicant detail`}
          className="rounded-app border border-border-default px-2.5 py-1.5 text-xs font-semibold text-brand-blue transition-colors hover:bg-brand-blueSoft focus-ring"
        >
          Open
        </button>
      </td>
    </tr>
  );
}

function ApplicantCard({ applicant, selected, selectable, onToggle, onOpen }) {
  const displayName = getApplicantDisplayName(applicant);
  const warning = getApplicantWarningLabel(applicant);
  const workflowLabel = getWorkflowStageLabel(applicant) || STAGE_LABELS[applicant.stage] || applicant.stage;
  const campaignBadge = getCampaignBadge(applicant.workflow);
  const next = getWorkflowNextAction(applicant);
  const lastTouch = getWorkflowLastTouch(applicant.workflow, applicant.last_contact);
  const interview = getWorkflowInterviewSummary(applicant.workflow);
  return (
    <article className="block w-full p-4 transition-colors hover:bg-surface-hover">
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={selected}
          disabled={!selectable}
          onChange={onToggle}
          aria-label={`Select ${displayName}`}
          className="mt-2 h-4 w-4 rounded border-border-strong text-brand-blue focus:ring-brand-blue disabled:opacity-40"
        />
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open ${displayName} applicant detail`}
          className="min-w-0 flex-1 rounded-app text-left focus-ring"
        >
          <span className="flex items-start justify-between gap-3">
            <span className="flex min-w-0 items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-blueSoft text-xs font-bold text-brand-blue ring-1 ring-info-border">
                {getApplicantInitials(applicant)}
              </span>
              <span className="min-w-0">
                <span className="block truncate font-semibold text-text-primary">{displayName}</span>
                <span className="mt-0.5 block text-xs text-text-muted">{applicant.position || applicant.role || 'Driver / Operator'} · {SOURCE_LABELS[applicant.source] || applicant.source || 'Manual'}</span>
                <span className="mt-1 block text-xs text-text-muted">{lastTouch}</span>
              </span>
            </span>
            <Badge variant={getApplicantStageBadgeVariant(applicant.workflow?.stageKey || applicant.stage)}>{workflowLabel}</Badge>
          </span>
          <span className="mt-3 block space-y-2 border-t border-border-default pt-3">
            <span className={cx('block text-sm font-semibold', next.className)}>{next.label}</span>
            <span className="flex flex-wrap gap-2 text-xs text-text-muted">
              {campaignBadge && <Badge variant={campaignBadge.variant}>{campaignBadge.label}</Badge>}
              <span>Interview: {interview}</span>
              <span className="ml-auto">Open →</span>
            </span>
          </span>
        </button>
      </div>
      {warning && <Alert variant="danger" className="mt-3">{warning}</Alert>}
      {!warning && applicant.workflow?.needsAttention && <Alert variant="warning" className="mt-3">Needs attention</Alert>}
    </article>
  );
}

function ApplicantProductivityBadges({ applicant }) {
  const { isStale, staleLabel, contactBlockedLabel } = getApplicantProductivityFlags(applicant);
  if (!isStale && !contactBlockedLabel) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {contactBlockedLabel && <Badge variant="danger" className="px-1.5 text-[11px]">{contactBlockedLabel}</Badge>}
      {isStale && <Badge variant="warning" className="px-1.5 text-[11px]">{staleLabel}</Badge>}
    </div>
  );
}

function Toast({ message }) {
  return (
    <div className="fixed right-4 top-4 z-[60] rounded-xl border border-success-border bg-success-bg px-4 py-3 text-sm font-semibold text-success-text shadow-popover" role="status">
      ✓ {message}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded-card border border-border-default bg-white shadow-popover">
        <div className="flex items-center justify-between border-b border-border-default px-5 py-4">
          <h3 className="font-semibold text-text-primary">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            className="rounded-app px-2 text-xl leading-none text-text-muted hover:bg-surface-hover hover:text-text-primary focus-ring"
          >
            ×
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
